import { type ReactElement } from 'react';
import { GoogleTagManager } from '@next/third-parties/google';
import { SWRConfig } from 'swr';
import Router from 'next/router';
import App, { type AppProps, type AppContext } from 'next/app';
import { type EmotionCache } from '@emotion/react';
import { captureException } from '@sentry/nextjs';
import { useSnackbar } from 'notistack';

import ProgressBar from '@badrap/bar-of-progress';

import UIThemeProvider from '../components/ui-theme-provider';
import Layout from '../components/layout';

import { createEmotionCache } from '../utils';

import 'bootstrap/dist/css/bootstrap-grid.min.css';
import 'bootstrap/dist/css/bootstrap-utilities.min.css';
import '../../styles/bootstrap/buttons.css';
import '../../styles/bootstrap/dropdown.css';
import '../../styles/styles.global.css';

import '../utils/wdyr';

const progress = new ProgressBar({
  size: 4,
  color: '#18598c',
  className: 'bar-of-progress',
  delay: 100,
});

Router.events.on('routeChangeStart', progress.start);
Router.events.on('routeChangeComplete', progress.finish);
Router.events.on('routeChangeError', progress.finish);

interface MUIAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

export default function MyApp(props: MUIAppProps): ReactElement {
  const { enqueueSnackbar } = useSnackbar();
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  if (pageProps.errorMessage) {
    enqueueSnackbar(pageProps.errorMessage, { variant: 'error' });
    captureException(new Error(pageProps.errorMessage));
  }

  return (
    <SWRConfig
      value={{
        revalidateOnFocus: false,
        revalidateOnMount: true,
        provider: () => new Map(),
        fallback: pageProps.fallback ?? {},
        keepPreviousData: true,
        onErrorRetry: (
          error,
          key,
          _config,
          revalidate,
          { retryCount },
        ) => {
          // Never retry on 404.
          if (error.status === 404) return;

          // Never retry for a specific key.
          if (key === '/api/v1/auth/me') return;

          // Only retry up to 10 times.
          if (retryCount >= 10) return;

          // Retry after 5 seconds.
          setTimeout(() => revalidate({ retryCount }), 5000);
        },
        fetcher: (resource, init) => fetch(resource, init)
          .then((res) => {
            if (!res.ok) {
              console.error(res.statusText);
              return { data: null };
            }
            return res.json();
          })
          .then(({ data }) => data),
      }}
    >
      <UIThemeProvider emotionCache={emotionCache}>
        <GoogleTagManager gtmId="GTM-PT57N7P" />
        <Layout>
          <Component {...pageProps} />
        </Layout>
      </UIThemeProvider>
    </SWRConfig>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);

  if (appContext.ctx.res?.statusCode === 404) {
    appContext.ctx.res.writeHead(302, { Location: '/not-found' });
    appContext.ctx.res.end();
  }

  return { ...appProps };
};
